import { useEffect } from 'react';
import { toast } from 'react-toastify';
import agent from '../api/agent';

export function useServicePriceFetching({
  serviceId,
  isRestoringDraft,
  shouldFetchPrice,
  restoredServiceId,
  order,
  setShouldFetchPrice,
  setRestoredServiceId,
  setUnitDiscountedPrice,
  draftRestorationRef,
  userRole = 'client' // Default to client role
}: {
  serviceId: number | undefined;
  isRestoringDraft: boolean;
  shouldFetchPrice: boolean;
  restoredServiceId: number | null;
  order?: any;
  setShouldFetchPrice: (value: boolean) => void;
  setRestoredServiceId: (value: number | null) => void;
  setUnitDiscountedPrice: (value: number) => void;
  draftRestorationRef: React.RefObject<boolean>;
  userRole?: 'client' | 'admin';
}) {
  // Effect to fetch price after draft restoration
  useEffect(() => {
    async function fetchServicePrice(serviceId: number) {
      try {
        // console.log(`Fetching price for restored serviceId (${userRole}):`, serviceId);
        const response = await agent.Services.price(serviceId);
        setUnitDiscountedPrice(response);
        // console.log('Service price set to:', response);
        toast.success('Pobrano cenę usługi');
      } catch (error) {
        console.error('Error fetching service price:', error);
        setUnitDiscountedPrice(0);
      } finally {
        setShouldFetchPrice(false);
        setRestoredServiceId(null);
      }
    }

    // When restoration is complete and we should fetch price
    if (!isRestoringDraft && shouldFetchPrice && restoredServiceId) {
      fetchServicePrice(restoredServiceId);
    }
  }, [isRestoringDraft, shouldFetchPrice, restoredServiceId, 
      setShouldFetchPrice, setRestoredServiceId, setUnitDiscountedPrice, userRole]);

  // Regular effect to fetch price when serviceId changes
  useEffect(() => {
    async function setServicePrice(serviceId: number) {
      try {
        // Skip the API call if serviceId is invalid
        if (!serviceId || isNaN(serviceId) || serviceId <= 0) {
          console.log(`Skipping price fetch - invalid serviceId (${userRole}):`, serviceId);
          setUnitDiscountedPrice(0);
          return;
        }

        // Skip when we're handling price fetching from the draft restoration
        if (shouldFetchPrice || isRestoringDraft || draftRestorationRef.current) {
          console.log(`Skipping regular price fetch due to draft restoration process (${userRole})`);
          return;
        }

        // console.log(`Fetching price for serviceId (${userRole}):`, serviceId);
        const response = await agent.Services.price(serviceId);
        setUnitDiscountedPrice(response);
        // console.log('Service price set to:', response);
      } catch (error) {
        console.error('Error fetching service price:', error);
        setUnitDiscountedPrice(0);
      }
    }

    // Only call if we have a valid serviceId and we're not in draft restoration process
    if (serviceId && !isRestoringDraft && !shouldFetchPrice && !draftRestorationRef.current) {
      // Parse serviceId to ensure it's a number
      const serviceIdNum = parseInt(serviceId.toString());
      if (!isNaN(serviceIdNum) && serviceIdNum > 0) {
        setServicePrice(serviceIdNum);
      } else {
        console.log(`Invalid serviceId for price fetch (${userRole}):`, serviceId);
        setUnitDiscountedPrice(0);
      }
    } else if (order?.unitDiscountedPrice) {
      // If we have an order with a price, use that
      setUnitDiscountedPrice(order.unitDiscountedPrice);
    }
  }, [serviceId, order?.unitDiscountedPrice, isRestoringDraft, 
      shouldFetchPrice, setUnitDiscountedPrice, draftRestorationRef, userRole]);
}